import React from "react";
import classes from "./WidgetCardsLandscape.module.css";

const getHeaderColor = (percent, isDarkTheme) => {
    const pct = parseFloat(String(percent || "0").replace('%', '')) || 0;
    const overTargetGray = isDarkTheme ? "rgb(75, 75, 75)" : "rgb(110, 110, 110)";
    if (pct >= 100) return overTargetGray;
    if (pct < 100 && pct >= 66.66) return "rgb(186, 77, 81)";
    if (pct < 66.66 && pct >= 33.33) return "rgb(232, 194, 103)";
    if (pct < 33.33) return "rgb(154, 181, 126)";
    return overTargetGray;
};

const getStackHeaderBorderColor = (headerColor) => {
    const match = headerColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return "rgba(255,255,255,0.2)";
    const factor = 0.68;
    return `rgb(${Math.round(parseInt(match[1]) * factor)}, ${Math.round(parseInt(match[2]) * factor)}, ${Math.round(parseInt(match[3]) * factor)})`;
};

const formatOwnerName = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0];

    // First word as is, rest as initials
    const formatted = [words[0]];
    for (let i = 1; i < words.length; i++) {
        formatted.push(words[i][0].toUpperCase());
    }
    return formatted.join(" ");
};

const EMPTY_CARD_STATE = {};

// Memoized card: with large ticket counts (hundreds of cards) a toggle used to
// re-render every card from scratch right before the open/close transition
// started, stealing the animation's first frames.  toggleCard now preserves
// referential identity for unchanged cardState objects, so React.memo lets
// untouched cards skip re-rendering entirely.
const LandscapeTicketCard = React.memo(function LandscapeTicketCard({
    card,
    index,
    cardState,
    isLastCard,
    isDarkTheme,
    themeStyles,
    compactStatusStyle,
    entryAnimStage,
    isReady,
    disableBodyTransition,
    disableHeaderTransition,
    isOpenTicketPlacementTop,
    imageError,
    onToggle,
    onImageError,
    hasUserInteractedRef,
    isEntryAnimatingRef,
    cardRefs,
}) {
    const isExpanded = Boolean(cardState.isExpanded);
    const isHalfOpen = Boolean(cardState.isHalfOpen);
    const renderMode = (cardState.renderMode === "open" || isExpanded || isHalfOpen)
        ? "open"
        : (cardState.renderMode === "medium" ? "medium" : "collapsed");
    const isCollapsedMode = renderMode === "collapsed";
    const isMediumMode = renderMode === "medium";
    const isBodyOpen = renderMode === "open";
    const cardStaggerDelay = Math.min(index, 8) * 55;
    const bodyAnimation = "none";
    const fullWidth = Number(cardState.fullHeight || 0);
    const visibleBodyWidth = Number(cardState.visibleBodyHeight || 0);
    const bodyWidth = renderMode === "open"
        ? `${Math.max(0, Math.max(1, visibleBodyWidth || fullWidth)).toFixed(3)}px`
        : "0px";
    const bodyDisplay = "flex";
    const bodyPadding = renderMode === "open" ? `${compactStatusStyle.bodyPaddingPx}px` : "0";
    const bodyMargin = "0";
    const contentOpacity = isBodyOpen ? 1 : 0;
    const contentTransitionDisabled = ((entryAnimStage === 1 || entryAnimStage === 2) && !hasUserInteractedRef.current) || disableBodyTransition;
    const contentTransition = (delayMs) => contentTransitionDisabled ? "none" : `opacity 680ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`;
    const normalHeaderPadPx = 0;
    const normalHeaderMinWidthPx = 26; // matches defaultOpenHeaderBlockPx in calc
    const normalHeaderFontRem = compactStatusStyle.normalHeaderFontRem;

    const mediumHeaderPadPx = normalHeaderPadPx;
    const mediumHeaderMinWidthPx = normalHeaderMinWidthPx;
    const mediumHeaderFontRem = normalHeaderFontRem;

    const collapsedHeaderPadPx = 0; // Math.max(0, mediumHeaderPadPx - 1);
    const collapsedHeaderMinWidthPx = 3; // Math.max(4, mediumHeaderMinWidthPx - 5);
    const collapsedHeaderFontRem = Math.max(0.44, Number((mediumHeaderFontRem - 0.18).toFixed(2)));

    const headerPadPx = isCollapsedMode
        ? collapsedHeaderPadPx
        : isMediumMode
            ? mediumHeaderPadPx
            : normalHeaderPadPx;
    const headerFontSize = isCollapsedMode
        ? `${collapsedHeaderFontRem}rem`
        : isMediumMode
            ? `${mediumHeaderFontRem}rem`
            : `${normalHeaderFontRem}rem`;
    const headerLineHeight = isCollapsedMode ? 0.5 : isMediumMode ? 0.7 : 1;

    const headerDisplayText = `${isCollapsedMode ? "" : (card.headerText || "")}`;
    const headerColor = getHeaderColor(card.percent, isDarkTheme);
    const percentStr = card.percent || "";
    const headerWidthPx = isCollapsedMode
        ? collapsedHeaderMinWidthPx
        : isMediumMode
            ? mediumHeaderMinWidthPx
            : normalHeaderMinWidthPx;

    return (
        <div
            className={`card ${classes.landscapeCard}`}
            ref={(el) => (cardRefs.current[index] = el)}
            style={{
                backgroundColor: themeStyles.cardBackground,
                border: isCollapsedMode ? "none" : themeStyles.cardBorder,
                boxShadow: isCollapsedMode ? "none" : themeStyles.cardBoxShadow,
                color: themeStyles.bodyTextColor,
                borderRadius: isCollapsedMode || isMediumMode ? "0" : "5px",
                opacity: isReady ? undefined : 0,
                animation: (entryAnimStage === 1 && isReady && !hasUserInteractedRef.current)
                    ? `widgetCardAppear 1ms step-end ${cardStaggerDelay}ms both`
                    : "none",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                flex: "0 0 auto",
                maxWidth: "none",
                minWidth: "0px",
                height: isCollapsedMode ? !isDarkTheme ? "calc(100% - 15px)" : "calc(100% - 16px)" : "calc(100% - 16px)",
                minHeight: 0,
                boxSizing: "border-box",
                overflow: "hidden",
                marginTop: isCollapsedMode ? !isDarkTheme ? "8px" : "9px" : "8px",
                marginBottom: "8px",
                // Promote each card to its own compositor layer — during toggles the
                // cards beside the toggled one shift every frame, and without layers
                // the whole shifted region repaints per frame (dropped frames at
                // large ticket counts).  See WidgetCards.js for measurements.
                willChange: "transform",
                // Minimal gap between collapsed cards and the open/medium block, based on current render state
                marginRight: (() => {
                    const cardGapPx = isDarkTheme ? "5px" : "3px";
                    return isCollapsedMode
                        ? (cardState.lastCollapsedMode ? (isOpenTicketPlacementTop ? (cardState.toggledEnabled ? cardGapPx : "0px") : cardGapPx) : "0px")
                        : (isLastCard ? "0" : cardGapPx);
                })(),
            }}
            onMouseEnter={(event) => {
                if (!isReady || isEntryAnimatingRef.current) return;
                event.currentTarget.style.animation = "none";
                event.currentTarget.style.transition = "transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1)";
                event.currentTarget.style.transform = "translateY(-3px)";
            }}
            onMouseLeave={(event) => {
                if (!isReady || isEntryAnimatingRef.current) return;
                event.currentTarget.style.transition = "transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
                event.currentTarget.style.transform = "translateY(0px)";
            }}
        >
            <div
                className={`card-header fw-bold ${classes.landscapeHeader}`}
                style={{
                    cursor: "pointer",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingTop: isCollapsedMode ? "0px" : `${compactStatusStyle.headerTopPadPx}px`,
                    paddingBottom: isCollapsedMode ? "0px" : `${compactStatusStyle.headerTopPadPx}px`,
                    paddingRight: `${headerPadPx}px`,
                    paddingLeft: `${headerPadPx}px`,
                    minHeight: 0,
                    boxSizing: "border-box",
                    fontSize: headerFontSize,
                    lineHeight: headerLineHeight,
                    backgroundColor: headerColor,
                    border: isCollapsedMode ? !isDarkTheme ? `1px solid #fff` : `1px solid ${getStackHeaderBorderColor(headerColor)}` : `1px solid ${headerColor}`,
                    color: compactStatusStyle.showHeaderText ? "#ffffff" : "transparent",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "auto",
                    textRendering: "geometricPrecision",
                    letterSpacing: "0.02em",
                    borderTopLeftRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                    borderTopRightRadius: isCollapsedMode || isMediumMode ? "0" : "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    alignSelf: "stretch",
                    textAlign: "center",

                    transition: disableHeaderTransition
                        ? "color 250ms ease"
                        : (() => {
                            const hMs = (entryAnimStage === 2 && !hasUserInteractedRef.current) ? 220 : 420;
                            return `color 250ms ease, flex-basis ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), max-width ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), min-width ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), font-size ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), padding ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), line-height ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), border-radius ${hMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
                        })(),
                    flex: `0 0 ${headerWidthPx}px`,
                    maxWidth: `${headerWidthPx}px`,
                    minWidth: `${headerWidthPx}px`,
                    height: "100%",
                    maxHeight: "100%",
                    wordBreak: "normal",
                    writingMode: "vertical-rl",
                    textOrientation: "mixed",
                    transform: "rotate(180deg)",
                }}
                title={headerDisplayText}
                onClick={() => onToggle(index)}
            >
                {!isCollapsedMode && (
                    <>
                        <span style={{ flex: "0 0 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxHeight: "60%" }}>
                            &nbsp;&nbsp;&nbsp;&nbsp;{headerDisplayText}
                        </span>
                        {percentStr && (
                            <span style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>
                                {percentStr + " of Target"}&nbsp;
                            </span>
                        )}
                    </>
                )}
            </div>

            <div
                className={`card-body collapse show ${classes.landscapeBody}`}
                style={{
                    backgroundColor: themeStyles.cardBackground,
                    color: themeStyles.bodyTextColor,
                    overflow: "hidden",
                    animation: bodyAnimation,
                    transition: disableBodyTransition ? "none" : "flex-basis 900ms cubic-bezier(0.34, 1.08, 0.64, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
                    maxHeight: "100%",
                    flexBasis: bodyWidth,
                    minWidth: 0,
                    display: bodyDisplay,
                    padding: bodyPadding,
                    margin: bodyMargin,
                    opacity: isBodyOpen ? 1 : 0,
                    pointerEvents: (isBodyOpen && entryAnimStage === 0) ? "auto" : "none",
                    borderRadius: "0",
                    flexGrow: 0,
                    flexShrink: 0,
                    alignSelf: "stretch",
                    boxSizing: "border-box",
                    contain: "layout paint",
                    // Compositing hints only for medium/open cards — promoting every
                    // collapsed stripe (hundreds at large ticket counts) creates a
                    // layer explosion that stutters the toggle animation.
                    transform: isCollapsedMode ? "none" : "translateZ(0)",
                    backfaceVisibility: "hidden",
                    willChange: (!disableBodyTransition && !isCollapsedMode) ? "flex-basis, opacity" : "auto",
                }}
            >
                {/* Collapsed stripes skip body content — see WidgetCards.js.
                    Avoids rendering hundreds of hidden DOM nodes for large
                    ticket counts, which stutters parallel entry animations. */}
                {!isCollapsedMode && (<>
                <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionTop}`}>
                    <div
                        className="row align-items-start"
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            marginLeft: 0,
                            marginRight: 0,
                            width: "100%",
                            opacity: contentOpacity,
                            transition: contentTransition(200),
                        }}
                    >
                        <div className="col text-start" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                            <div style={{ fontSize: `${compactStatusStyle.topLeftFontRem}rem`, fontWeight: "bold" }}>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    Target: {card.targetHours != null && !isNaN(Number(card.targetHours)) ? +Number(card.targetHours).toFixed(2) : "NA"} hrs
                                </div>
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    Actual: {card.actualHours != null && !isNaN(Number(card.actualHours)) ? +Number(card.actualHours).toFixed(2) : "NA"} hrs
                                </div>
                            </div>
                        </div>

                        <div className="col d-flex flex-column align-items-end" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: 0, paddingRight: 0 }}>
                            <div style={{ width: `${compactStatusStyle.avatarWrapWidthPx}px`, marginLeft: "auto", display: "flex", justifyContent: "center" }}>
                                {!card.imageUrl || imageError ? (
                                    <svg
                                        width={compactStatusStyle.avatarSizePx}
                                        height={compactStatusStyle.avatarSizePx}
                                        viewBox="0 0 200 200"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ borderRadius: "50%" }}
                                    >
                                        <circle cx="100" cy="100" r="78" fill="#EBCDB2" stroke="#E3B187" strokeWidth="1.5" />
                                        <circle cx="100" cy="68" r="26" fill="#B8B8B8" />
                                        <path d="M72 100 L128 100 L100 178 Z" fill="#B8B8B8" />
                                    </svg>
                                ) : (
                                    <img
                                        src={card.imageUrl}
                                        alt="avatar"
                                        style={{
                                            width: compactStatusStyle.avatarSizePx,
                                            height: compactStatusStyle.avatarSizePx,
                                            borderRadius: "50%",
                                        }}
                                        onError={() => onImageError(card.id)}
                                    />
                                )}
                            </div>

                            <div
                                style={{
                                    width: `${compactStatusStyle.avatarWrapWidthPx}px`,
                                    marginLeft: "auto",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textAlign: "center",
                                    color: themeStyles.bodyTextColor,
                                    fontSize: `${compactStatusStyle.ownerFontRem}rem`,
                                }}
                                title={card.owner}
                            >
                                {formatOwnerName(card.owner)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionMiddle}`}>
                    <div className={classes.landscapeBodyStack}>
                        <p
                            className="fw-bold text-center"
                            style={{
                                display: "-webkit-box",
                                WebkitLineClamp: compactStatusStyle.titleLines,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: `${compactStatusStyle.titleFontRem}rem`,
                                lineHeight: 1.2,
                                maxHeight: `${(compactStatusStyle.titleFontRem * 1.2 * compactStatusStyle.titleLines + 0.1).toFixed(2)}rem`,
                                flexShrink: 0,
                                marginBottom: `${compactStatusStyle.titleMarginBottomRem}rem`,
                                opacity: contentOpacity,
                                transition: contentTransition(360),
                            }}
                            title={card.title}
                        >
                            {card.title}
                        </p>
                        <p
                            className="mt-1 text-center"
                            style={{
                                color: themeStyles.mutedTextColor,
                                display: "-webkit-box",
                                WebkitLineClamp: compactStatusStyle.descLines,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: `${compactStatusStyle.descFontRem}rem`,
                                lineHeight: 1.2,
                                maxHeight: `${(compactStatusStyle.descFontRem * 1.2 * compactStatusStyle.descLines + 0.1).toFixed(2)}rem`,
                                flexShrink: 0,
                                marginBottom: `${compactStatusStyle.descMarginBottomRem}rem`,
                                opacity: contentOpacity,
                                transition: contentTransition(520),
                            }}
                            title={card.description}
                        >
                            {card.description}
                        </p>
                        {card.description1 && (
                            <p
                                className="text-center"
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    color: themeStyles.mutedTextColor,
                                    fontSize: `${compactStatusStyle.descFontRem}rem`,
                                    lineHeight: 1.2,
                                    marginTop: "0",
                                    marginBottom: `${compactStatusStyle.titleMarginBottomRem}rem`,
                                    opacity: contentOpacity,
                                    transition: contentTransition(440),
                                }}
                                title={card.description1}
                            >
                                {card.description1}
                            </p>
                        )}

                    </div>
                </div>

                <div className={`${classes.landscapeBodySection} ${classes.landscapeBodySectionBottom}`}>
                    <div
                        className="row small"
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            marginLeft: 0,
                            marginRight: 0,
                            width: "100%",
                            fontSize: `${compactStatusStyle.metaFontRem}rem`,
                            lineHeight: 1.2,
                            paddingTop: `${compactStatusStyle.metaRowPadPx}px`,
                            paddingBottom: `${compactStatusStyle.bodyPaddingPx + 7}px`,
                            opacity: contentOpacity,
                            transition: contentTransition(680),
                        }}
                    >
                        <div className="col text-start" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: `${compactStatusStyle.metaRowPadPx}px`, paddingRight: `${compactStatusStyle.metaRowPadPx}px` }}>
                            <b>Opened</b>
                            <br />
                            {card.openDate ?? "NA"}
                        </div>
                        <div className="col text-center" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: `${compactStatusStyle.metaRowPadPx}px`, paddingRight: `${compactStatusStyle.metaRowPadPx}px` }}>
                            <div style={{ display: "inline-block", textAlign: "left" }}>
                                <b>Promised</b>
                                <br />
                                {card.promiseDate ?? "NA"}
                            </div>
                        </div>
                        <div className="col text-end" style={{ flex: "1 1 0", minWidth: 0, maxWidth: "none", paddingLeft: `${compactStatusStyle.metaRowPadPx}px`, paddingRight: `${compactStatusStyle.metaRowPadPx}px` }}>
                            <div style={{ display: "inline-block", textAlign: "left" }}>
                                <b>Completed</b>
                                <br />
                                {card.completedDate ?? card.status ?? "NA"}
                            </div>
                        </div>
                    </div>
                </div>
                </>)}
            </div>
        </div>
    );
});

export { EMPTY_CARD_STATE };
export default LandscapeTicketCard;
